import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const num = (v, d = 2) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: d });
const tone = (v) => (Number(v) >= 0 ? "text-emerald-400" : "text-rose-400");
const TH = "text-left px-3 py-2 text-[10px] uppercase text-slate-500";

export function PositionsTable({ positions }) {
  const qc = useQueryClient();
  const close = useMutation({
    mutationFn: async (p) => (await api.post(`/paper/positions/${p.ccy}/${p.symbol}/close`)).data,
    onSuccess: (o) => { qc.invalidateQueries({ queryKey: ["paper"] }); toast.success(`Closed ${o.symbol} · ${o.status}`); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  if (!positions.length) return <div className="p-6 text-xs text-slate-500" data-testid="positions-empty">No open paper positions.</div>;
  return (
    <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="positions-table">
      <thead><tr><th className={TH}>Symbol</th><th className={TH}>Qty</th><th className={TH}>Avg</th><th className={TH}>Last</th><th className={TH}>Stop / Target</th><th className={TH}>Unrealized</th><th className={TH}></th></tr></thead>
      <tbody>{positions.map((p) => (
        <tr key={`${p.ccy}-${p.symbol}`} className="border-t border-[var(--border-subtle)]" data-testid={`position-row-${p.symbol}`}>
          <td className="px-3 py-2 font-semibold text-slate-200">{p.symbol}<span className="text-slate-500 ml-1">{p.ccy}</span></td>
          <td className="px-3 font-mono-num">{p.qty}</td><td className="px-3 font-mono-num">{num(p.avg_price, 4)}</td>
          <td className="px-3 font-mono-num">{num(p.last_price, 4)}</td>
          <td className="px-3 font-mono-num text-slate-400">{p.stop_loss || "—"} / {p.take_profit || "—"}</td>
          <td className={cn("px-3 font-mono-num", tone(p.unrealized_pnl))}>{num(p.unrealized_pnl)}</td>
          <td className="px-3"><Button size="sm" variant="outline" data-testid={`close-position-${p.symbol}`} onClick={() => close.mutate(p)} className="h-7 text-[11px] border-[var(--border-subtle)]">Close</Button></td>
        </tr>))}</tbody>
    </table></div>
  );
}

export function OrdersTable({ orders }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: async (id) => (await api.post(`/paper/orders/${id}/cancel`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["paper"] }); toast.success("Order cancelled"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const st = { FILLED: "text-emerald-400", OPEN: "text-amber-400", PARTIALLY_FILLED: "text-sky-400", FILLING: "text-sky-400", REJECTED: "text-rose-400", CANCELLED: "text-slate-500" };
  if (!orders.length) return <div className="p-6 text-xs text-slate-500" data-testid="orders-empty">No paper orders yet.</div>;
  return (
    <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="orders-table">
      <thead><tr><th className={TH}>Time</th><th className={TH}>Order</th><th className={TH}>Type</th><th className={TH}>Status</th><th className={TH}>Fill / Reason</th><th className={TH}></th></tr></thead>
      <tbody>{orders.map((o) => (
        <tr key={o.id} className="border-t border-[var(--border-subtle)]" data-testid={`order-row-${o.id}`}>
          <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{o.created_at?.slice(5, 19).replace("T", " ")}</td>
          <td className="px-3 text-slate-200">{o.side} {o.qty} {o.symbol}{o.filled_qty && o.filled_qty !== o.qty ? ` (filled ${o.filled_qty})` : ""}</td>
          <td className="px-3 text-slate-400">{o.order_type}{o.limit_price ? ` @ ${o.limit_price}` : ""}{o.system ? " · auto" : ""}</td>
          <td className={cn("px-3 font-semibold", st[o.status])}>{o.status}</td>
          <td className="px-3 text-slate-400 max-w-[320px] truncate" title={o.reject_reason}>{o.fill_price ? `${o.fill_price} · fee ${o.fee}` : o.reject_reason || o.reason}</td>
          <td className="px-3">{(o.status === "OPEN" || o.status === "PARTIALLY_FILLED") && <Button size="sm" variant="outline" data-testid={`cancel-order-${o.id}`} onClick={() => cancel.mutate(o.id)} className="h-7 text-[11px] border-[var(--border-subtle)]">Cancel</Button>}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

export function LedgerTable({ ledger }) {
  if (!ledger) return null;
  const tb = ledger.trial_balance;
  return (
    <div className="p-3 space-y-3" data-testid="paper-ledger">
      <div className="text-xs text-slate-400">Reconciliation: <span data-testid="paper-recon-status" className={ledger.reconciliation.status === "reconciled" ? "text-emerald-400" : "text-rose-400"}>{ledger.reconciliation.status}</span>
        {" · "}Trial balance {tb.balanced ? "balanced" : "UNBALANCED"} (Dr {num(tb.total_debit)} / Cr {num(tb.total_credit)})</div>
      <table className="w-full text-xs" data-testid="paper-trial-balance"><tbody>{tb.rows.map((r) => (
        <tr key={r.code} className="border-t border-[var(--border-subtle)]"><td className="px-3 py-1.5 font-mono text-slate-500">{r.code}</td><td className="px-3 text-slate-300">{r.name}</td>
          <td className="px-3 text-right font-mono-num">{num(r.debit)}</td><td className="px-3 text-right font-mono-num">{num(r.credit)}</td></tr>))}</tbody></table>
      <div className="max-h-[260px] overflow-y-auto">{ledger.entries.slice(0, 50).map((e) => (
        <div key={e.id} className="border-t border-[var(--border-subtle)] px-3 py-1.5 text-[11px] text-slate-400">{e.date.slice(0, 19).replace("T", " ")} · {e.narration}</div>))}</div>
    </div>
  );
}
